import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  IContractData,
  IContractResponse,
  IInfoClientAvailableCredit,
  IListOfferAvailableCredit,
  IResponseAvailableCredit,
  ISaveClientInfo,
  OtpResponseValidation,
  ClientInfoResponse,
  ISwitchPaymentOption,
} from '../../interfaces/validations.interface';
import { ISteps } from '../../interfaces/steps.interface';
import { IInfoOtp } from '../../interfaces/alert.interface';
import {
  SERVICE_TYPE,
  ServiceType,
} from 'src/app/core/constants/service-type.constant';

@Injectable({
  providedIn: 'root',
})
export class StepsService {
  private readonly router = inject(Router);

  private _responseOtpDn: OtpResponseValidation | null = null;
  private _saveClientInfo: ISaveClientInfo | null = null;
  private _clientInfoResponse: ClientInfoResponse | null = null;
  private _idAutentix: string = '';
  private _infoOtp: IInfoOtp = { phoneContact: '', idDn: '' };
  private _bodyAvailableCredit: IInfoClientAvailableCredit | null = null;
  private _listOffersAvailableCredit: IResponseAvailableCredit | null = null;
  private _listOfferSelected: IListOfferAvailableCredit | null = null;
  private _imei: string = '';
  private _nip: string = '';
  private _dataContractClient: IContractData | null = null;
  private _tokenLoginIncode: string = '';

  baitDn: string = '';
  uuidContract: string = '';
  idPreRegister: string = '';
  serviceType: string = '';
  typeService: ServiceType | '' = '';
  userInfoContract!: IContractResponse;
  recurringHiring: boolean = false;
  bail: number = 0;
  planIndex: number = 0;

  private _paymentNotRequired: boolean = false;
  private _paymentOption: ISwitchPaymentOption | null = null;

  private readonly _stepsSubject = new BehaviorSubject<ISteps[]>([
    {
      id: 'compatibilidad',
      title: 'Valida la compatibilidad de tu equipo',
      status: 1,
      style: 'compatibility',
    },
    {
      id: 'valida-numero',
      title: 'Comparte tu número',
      status: 0,
      style: 'number',
    },
    {
      id: 'verifica-identidad',
      title: 'Verifica tu identidad',
      status: 0,
      style: 'identity',
    },
    {
      id: 'ingresa-datos',
      title: 'Ingresa tus datos',
      status: 0,
      style: 'data',
    },
    {
      id: 'consulta-buro',
      title: 'Consulta de historial en Buró de Crédito',
      status: 0,
      style: 'credit-history',
    },
    { id: 'elige-plan', title: 'Elige tu plan', status: 0, style: 'plan' },
    {
      id: 'firma-contrato',
      title: 'Firma tu contrato',
      status: 0,
      style: 'signature',
    },
  ]);

  public readonly steps$ = this._stepsSubject.asObservable();

  get nip(): string {
    return this._nip;
  }
  set nip(nip: string) {
    this._nip = nip;
  }

  get isPortabilityFlow(): boolean {
    return this.serviceType === SERVICE_TYPE.PORTABILITY;
  }

  get isMigrated(): boolean {
    return this.typeService === SERVICE_TYPE.MIGRATED;
  }

  get isNew(): boolean {
    return this.typeService === SERVICE_TYPE.NEW;
  }

  get paymentNotRequired(): boolean {
    return this._paymentNotRequired;
  }
  set paymentNotRequired(value: boolean) {
    this._paymentNotRequired = value;
  }

  get paymentOption(): ISwitchPaymentOption | null {
    return this._paymentOption;
  }
  set paymentOption(value: ISwitchPaymentOption | null) {
    this._paymentOption = value;
  }

  setStep(id: string): void {
    const nextIndex = this._stepsSubject.value.findIndex(
      (step) => step.id === id,
    );

    const updatedSteps = this._stepsSubject.value.map((step, idx) => {
      let stepStatus = 0;

      if (idx < nextIndex) {
        stepStatus = 2;
      } else if (idx === nextIndex) {
        stepStatus = 1;
      }

      return {
        ...step,
        status: stepStatus,
      };
    });

    this._stepsSubject.next(updatedSteps);
  }

  updateStepTitle(): void {
    const updatedSteps = this._stepsSubject.value.map((step) => ({
      ...step,
      title:
        this.isPortabilityFlow && step.id === 'compatibilidad'
          ? 'Realiza tu compatibilidad y portabilidad'
          : step.title,
    }));

    this._stepsSubject.next(updatedSteps);
  }

  getCurrentStep(): Observable<ISteps[]> {
    return this.steps$;
  }

  resetSteps(): void {
    this._paymentNotRequired = false;
    this._paymentOption = null;

    const resetSteps = this._stepsSubject.value.map((step, index) => ({
      ...step,
      status: index === 0 ? 1 : 0,
    }));
    this._stepsSubject.next(resetSteps);
  }

  getDataOtp(): IInfoOtp {
    return this._infoOtp;
  }
  setDataOtp(dnIdDn: IInfoOtp): void {
    this._infoOtp = { ...dnIdDn };
  }

  getInfoOtpDn(): OtpResponseValidation | null {
    return this._responseOtpDn;
  }
  setInfoOtpDn(data: OtpResponseValidation): void {
    this._responseOtpDn = data;
  }

  getIdAutentix(): string {
    return this._idAutentix;
  }
  setIdAutentix(sessionId: string): void {
    this._idAutentix = sessionId;
  }

  getClientInfo(): ClientInfoResponse | null {
    return this._clientInfoResponse;
  }
  setClientInfo(clientInfo: ClientInfoResponse): void {
    this._clientInfoResponse = clientInfo;
  }

  getBodyAvailableCredit(): IInfoClientAvailableCredit | null {
    return this._bodyAvailableCredit;
  }
  setBodyAvailableCredit(infoClient: IInfoClientAvailableCredit): void {
    this._bodyAvailableCredit = infoClient;
  }

  getListOffersAvailableCredit(): IResponseAvailableCredit | null {
    return this._listOffersAvailableCredit;
  }
  setListOffersAvailableCredit(offersList: IResponseAvailableCredit): void {
    this._listOffersAvailableCredit = offersList;
  }

  getListOfferSelected(): IListOfferAvailableCredit | null {
    return this._listOfferSelected;
  }
  setListOfferSelected(offerSelected: IListOfferAvailableCredit): void {
    this._listOfferSelected = offerSelected;
  }

  getImei(): string {
    return this._imei;
  }
  setImei(data: string): void {
    this._imei = data;
  }

  getDataContractClient(): IContractData | null {
    return this._dataContractClient;
  }
  setDataContractClient(data: IContractData): void {
    this._dataContractClient = data;
  }

  getUserDataInformation(): ISaveClientInfo | null {
    return this._saveClientInfo;
  }
  setUserDataInformation(clientData: ISaveClientInfo): void {
    this._saveClientInfo = clientData;
  }

  setIndex(i: number): void {
    this.planIndex = i;
  }
  getIndex(): number {
    return this.planIndex;
  }

  getTokenLoginIncode(): string {
    return this._tokenLoginIncode;
  }
  setTokenLoginIncode(token: string): void {
    this._tokenLoginIncode = token;
  }

  backToHome(): void {
    this.router.navigateByUrl('/');
    this.resetSteps();
  }
}
